import {
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleRounded,
  CloseRounded,
  RouteRounded,
  TimelineRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  applyIndustrialWorkflowCaseStageScheduleOverride,
  getIndustrialWorkflowCaseStageSchedule,
  previewIndustrialWorkflowCaseStageScheduleOverride,
} from '../services/industrialWorkflowsService';
import {
  formatOperationalDate,
} from './IndustrialWorkflowDatePicker';
import type {
  IndustrialWorkflowCase,
  IndustrialWorkflowCaseScheduleItem,
  IndustrialWorkflowCaseStageSchedule,
  IndustrialWorkflowCaseStageScheduleOverridePreview,
} from '../types/industrialWorkflows';

type Props = {
  workflowCase: IndustrialWorkflowCase | null;
  open: boolean;
  onClose: () => void;
};

const DAY_MS = 24 * 60 * 60 * 1000;

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  return error instanceof Error
    ? error.message
    : 'No se pudo cargar la planificación.';
}

function parseDateOnly(
  value: string | null | undefined,
) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(
    value ?? '',
  );

  if (!match) return null;

  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
}

function getDayNumber(value: Date) {
  return Date.UTC(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
  ) / DAY_MS;
}

function getLocalDateOnly() {
  const now = new Date();

  return [
    now.getFullYear(),
    String(
      now.getMonth() + 1,
    ).padStart(2, '0'),
    String(
      now.getDate(),
    ).padStart(2, '0'),
  ].join('-');
}

function formatDayNumber(value: number) {
  return formatOperationalDate(
    new Date(
      value * DAY_MS,
    )
      .toISOString()
      .slice(0, 10),
  );
}

function getDifferenceInDays(
  fromValue: string | null | undefined,
  toValue: string | null | undefined,
) {
  const fromDate =
    parseDateOnly(fromValue);

  const toDate =
    parseDateOnly(toValue);

  return fromDate && toDate
    ? (
        getDayNumber(toDate)
        - getDayNumber(fromDate)
      )
    : null;
}

function formatSlackDays(
  value: number | null | undefined,
  hasTarget: boolean,
) {
  if (!hasTarget) {
    return 'Sin objetivo';
  }

  if (
    value === null
    || value === undefined
  ) {
    return 'No disponible';
  }

  if (value < 0) {
    return `${Math.abs(
      value,
    )} días laborables`;
  }

  return `${value} días laborables`;
}

function getSlackMetricLabel(
  value: number | null | undefined,
) {
  return (
    value !== null
    && value !== undefined
    && value < 0
  )
    ? 'Retraso previsto'
    : 'Margen disponible';
}

function getSlackMetricHelper(
  value: number | null | undefined,
  hasTarget: boolean,
) {
  if (!hasTarget) {
    return 'Define un objetivo global para comparar';
  }

  return (
    value !== null
    && value !== undefined
    && value < 0
  )
    ? 'Supera el objetivo global'
    : 'Días laborables frente al objetivo';
}

function formatDeviationDays(
  value: number | null | undefined,
) {
  if (
    value === null
    || value === undefined
  ) {
    return 'No disponible';
  }

  if (value === 0) {
    return 'Sin desviación';
  }

  if (value < 0) {
    return `${Math.abs(
      value,
    )} ${
      Math.abs(value) === 1
        ? 'día'
        : 'días'
    } antes`;
  }

  return `${value} ${
    value === 1
      ? 'día'
      : 'días'
  } después`;
}

function readMetadataText(
  item: IndustrialWorkflowCaseScheduleItem,
  key: string,
) {
  const value =
    item.metadata?.[key];

  return typeof value === 'string'
    ? value
    : null;
}

function readMetadataNumber(
  item: IndustrialWorkflowCaseScheduleItem,
  key: string,
) {
  const value =
    item.metadata?.[key];

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const parsed =
      Number(value);

    return Number.isFinite(
      parsed,
    )
      ? parsed
      : null;
  }

  return null;
}

function readMetadataBoolean(
  item: IndustrialWorkflowCaseScheduleItem,
  key: string,
) {
  const value =
    item.metadata?.[key];

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value === 'true';
  }

  return false;
}

function getStandardDurationDays(
  item: IndustrialWorkflowCaseScheduleItem,
) {
  return readMetadataNumber(
    item,
    'expected_duration_days',
  );
}

function getForecastDurationDays(
  item: IndustrialWorkflowCaseScheduleItem,
) {
  return readMetadataNumber(
    item,
    'forecast_duration_days',
  );
}

function hasManualScheduleOverride(
  item: IndustrialWorkflowCaseScheduleItem,
) {
  return readMetadataBoolean(
    item,
    'has_manual_override',
  );
}

function getScheduleTone(
  schedule:
    IndustrialWorkflowCaseStageSchedule,
) {
  const status =
    schedule.active_version
      ?.calculation_status
    ?? schedule.schedule_status;

  if (status === 'conflicted') {
    return [
      'Planificación con conflictos',
      '#fecaca',
      '#ef4444',
    ] as const;
  }

  if (status === 'incomplete') {
    return [
      'Planificación incompleta',
      '#fde68a',
      '#f59e0b',
    ] as const;
  }

  if (status === 'not_generated') {
    return [
      'Planificación no generada',
      '#cbd5e1',
      '#64748b',
    ] as const;
  }

  if (!schedule.target_due_on) {
    return [
      'Sin objetivo global',
      '#cbd5e1',
      '#64748b',
    ] as const;
  }

  const slackDays =
    schedule.active_version
      ?.slack_days;

  if (
    slackDays !== null
    && slackDays !== undefined
    && slackDays < 0
  ) {
    return [
      'Fuera de objetivo',
      '#fecaca',
      '#ef4444',
    ] as const;
  }

  if (
    slackDays !== null
    && slackDays !== undefined
    && slackDays <= 5
  ) {
    return [
      'Margen reducido',
      '#fde68a',
      '#f59e0b',
    ] as const;
  }

  return [
    'Dentro de objetivo',
    '#bbf7d0',
    '#22c55e',
  ] as const;
}

function getStageTypeLabel(
  item:
    IndustrialWorkflowCaseScheduleItem,
) {
  const type =
    readMetadataText(
      item,
      'stage_type',
    );

  if (type === 'outside_system_wait') {
    return 'Espera externa';
  }

  if (type === 'closure') {
    return 'Cierre';
  }

  if (type === 'entry') {
    return 'Entrada';
  }

  return 'Trabajo interno';
}

function getStageRole(
  item:
    IndustrialWorkflowCaseScheduleItem,
) {
  const role =
    readMetadataText(
      item,
      'schedule_role',
    );

  if (role === 'completed') {
    return [
      'Completada',
      '#4ade80',
      '#22c55e',
    ] as const;
  }

  if (role === 'current') {
    return [
      'Etapa actual',
      '#7dd3fc',
      '#0284c7',
    ] as const;
  }

  return [
    'Prevista',
    '#c4b5fd',
    '#8b5cf6',
  ] as const;
}

function getOperationalDueOn(
  item:
    IndustrialWorkflowCaseScheduleItem,
) {
  const value =
    item.metadata?.[
      'operational_due_on'
    ];

  return typeof value === 'string'
    ? value
    : null;
}

function getScheduleRoleValue(
  item:
    IndustrialWorkflowCaseScheduleItem,
) {
  return (
    readMetadataText(
      item,
      'schedule_role',
    ) ?? 'future'
  );
}

function getForecastColor(
  item:
    IndustrialWorkflowCaseScheduleItem,
) {
  const role =
    readMetadataText(
      item,
      'schedule_role',
    );

  if (role === 'completed') {
    return '#4ade80';
  }

  if (role === 'current') {
    return '#38bdf8';
  }

  return (
    readMetadataText(
      item,
      'stage_type',
    )
    === 'outside_system_wait'
  )
    ? '#fbbf24'
    : '#a78bfa';
}

function Metric({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${alpha(
          '#94a3b8',
          0.14,
        )}`,
        bgcolor:
          'rgba(15,23,42,0.44)',
        p: 1,
      }}
    >
      <Typography
        sx={{
          color: '#94a3b8',
          fontSize: '0.66rem',
          fontWeight: 900,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: '#f8fafc',
          fontWeight: 950,
          mt: 0.25,
        }}
      >
        {value}
      </Typography>

      <Typography
        sx={{
          color: '#64748b',
          fontSize: '0.7rem',
          mt: 0.15,
        }}
      >
        {helper}
      </Typography>
    </Paper>
  );
}

export default function IndustrialWorkflowCasePlanningDialog({
  workflowCase,
  open,
  onClose,
}: Props) {
  const queryClient =
    useQueryClient();

  const [
    replanItem,
    setReplanItem,
  ] = useState<IndustrialWorkflowCaseScheduleItem | null>(
    null,
  );

  const [
    durationInput,
    setDurationInput,
  ] = useState('');

  const [
    reasonInput,
    setReasonInput,
  ] = useState('');

  const [
    overridePreview,
    setOverridePreview,
  ] =
    useState<IndustrialWorkflowCaseStageScheduleOverridePreview | null>(
      null,
    );

  const scheduleQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-case-schedule',
        workflowCase?.id,
      ],

      queryFn: () =>
        getIndustrialWorkflowCaseStageSchedule(
          workflowCase
            ?.id as string,
        ),

      enabled: Boolean(
        open
        && workflowCase?.id,
      ),

      retry: false,

      refetchOnMount:
        'always',
    });


  const schedule =
    scheduleQuery.data;

  const parseDurationInput = () => {
    const parsed =
      Number(
        durationInput,
      );

    if (
      !Number.isInteger(
        parsed,
      )
      || parsed < 0
    ) {
      throw new Error(
        'La duración prevista debe ser un número entero mayor o igual que cero.',
      );
    }

    return parsed;
  };

  const closeReplanDialog = () => {
    setReplanItem(
      null,
    );

    setDurationInput(
      '',
    );

    setReasonInput(
      '',
    );

    setOverridePreview(
      null,
    );
  };

  const openReplanDialog = (
    item: IndustrialWorkflowCaseScheduleItem,
  ) => {
    const durationDays =
      getForecastDurationDays(
        item,
      )
      ?? getStandardDurationDays(
        item,
      )
      ?? 0;

    setReplanItem(
      item,
    );

    setDurationInput(
      String(
        durationDays,
      ),
    );

    setReasonInput(
      '',
    );

    setOverridePreview(
      null,
    );
  };

  const previewOverrideMutation =
    useMutation({
      mutationFn: () => {
        if (
          !workflowCase?.id
          || !replanItem?.stage_definition_id
        ) {
          throw new Error(
            'No se ha podido identificar la etapa a replanificar.',
          );
        }

        return previewIndustrialWorkflowCaseStageScheduleOverride(
          workflowCase.id,
          {
            stage_definition_id:
              replanItem.stage_definition_id,

            duration_days:
              parseDurationInput(),
          },
        );
      },

      onSuccess: (
        preview,
      ) => {
        setOverridePreview(
          preview,
        );
      },
    });

  const applyOverrideMutation =
    useMutation({
      mutationFn: () => {
        if (
          !workflowCase?.id
          || !replanItem?.stage_definition_id
        ) {
          throw new Error(
            'No se ha podido identificar la etapa a replanificar.',
          );
        }

        const reason =
          reasonInput.trim();

        if (!reason) {
          throw new Error(
            'Debes indicar el motivo de la replanificación manual.',
          );
        }

        return applyIndustrialWorkflowCaseStageScheduleOverride(
          workflowCase.id,
          {
            stage_definition_id:
              replanItem.stage_definition_id,

            duration_days:
              parseDurationInput(),

            reason,
          },
        );
      },

      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-case-schedule',
            workflowCase?.id,
          ],
        });

        closeReplanDialog();
      },
    });

  const replanError =
    previewOverrideMutation.error
    ?? applyOverrideMutation.error;

  const stageItems =
    useMemo(
      () =>
        [
          ...(
            schedule
              ?.active_items
            ?? []
          ),
        ]
          .filter(
            (item) =>
              item.item_type
              === 'stage',
          )
          .sort(
            (
              left,
              right,
            ) =>
              left.display_order
              - right
                .display_order,
          ),

      [
        schedule
          ?.active_items,
      ],
    );

  const completedStageItems =
    stageItems.filter(
      (item) =>
        getScheduleRoleValue(
          item,
        ) === 'completed',
    );

  const currentStageItems =
    stageItems.filter(
      (item) =>
        getScheduleRoleValue(
          item,
        ) === 'current',
    );

  const futureStageItems =
    stageItems.filter(
      (item) =>
        getScheduleRoleValue(
          item,
        ) === 'future',
    );

  const baselineCompletion =
    schedule
      ?.baseline_version
      ?.projected_completion_on
    ?? null;

  const projectedCompletion =
    schedule
      ?.active_version
      ?.projected_completion_on
    ?? null;

  const deviationDays =
    getDifferenceInDays(
      baselineCompletion,
      projectedCompletion,
    );

  const range =
    useMemo(
      () => {
        const values = [
          parseDateOnly(
            getLocalDateOnly(),
          ),

          parseDateOnly(
            schedule
              ?.target_due_on,
          ),

          ...stageItems.flatMap(
            (item) => [
              parseDateOnly(
                item.planned_start_on,
              ),

              parseDateOnly(
                item.planned_due_on,
              ),

              parseDateOnly(
                item.forecast_start_on,
              ),

              parseDateOnly(
                item.forecast_due_on,
              ),
            ],
          ),
        ].filter(
          (
            value,
          ): value is Date =>
            Boolean(
              value,
            ),
        );

        const days =
          values.map(
            getDayNumber,
          );

        const min =
          Math.min(
            ...days,
          );

        const max =
          Math.max(
            ...days,
          );

        return {
          min,

          max:
            min === max
              ? min + 1
              : max,
        };
      },

      [
        schedule
          ?.target_due_on,

        stageItems,
      ],
    );

  const getPosition = (
    value:
      string
      | null
      | undefined,
  ) => {
    const parsed =
      parseDateOnly(
        value,
      );

    if (!parsed) {
      return null;
    }

    return Math.min(
      100,

      Math.max(
        0,

        (
          (
            getDayNumber(
              parsed,
            )
            - range.min
          )
          / (
            range.max
            - range.min
          )
        )
        * 100,
      ),
    );
  };

  const getBarStyle = (
    startValue:
      string
      | null,

    dueValue:
      string
      | null,
  ) => {
    const start =
      getPosition(
        startValue,
      );

    const due =
      getPosition(
        dueValue,
      );

    return (
      start === null
      || due === null
    )
      ? null
      : {
          left:
            `${
              Math.min(
                start,
                due,
              )
            }%`,

          width:
            `${
              Math.max(
                Math.abs(
                  due - start,
                ),

                1.4,
              )
            }%`,
        };
  };

  const todayPosition =
    getPosition(
      getLocalDateOnly(),
    );

  const targetPosition =
    getPosition(
      schedule
        ?.target_due_on,
    );

  const projectedPosition =
    getPosition(
      projectedCompletion,
    );

  const timelineTicks =
    useMemo(
      () => {
        const ticks: Array<{
          value: string;
          label: string;
          position: number;
          isMonthStart: boolean;
        }> = [];

        const startDay =
          Math.ceil(
            range.min,
          );

        const endDay =
          Math.floor(
            range.max,
          );

        for (
          let day = startDay;
          day <= endDay;
          day += 1
        ) {
          const value =
            new Date(
              day * DAY_MS,
            )
              .toISOString()
              .slice(
                0,
                10,
              );

          const dayOfMonth =
            value.slice(
              8,
              10,
            );

          if (
            dayOfMonth !== '01'
            && dayOfMonth !== '15'
          ) {
            continue;
          }

          ticks.push({
            value,

            label:
              formatOperationalDate(
                value,
              ),

            position:
              Math.min(
                100,

                Math.max(
                  0,

                  (
                    (
                      day
                      - range.min
                    )
                    / (
                      range.max
                      - range.min
                    )
                  )
                  * 100,
                ),
              ),

            isMonthStart:
              dayOfMonth === '01',
          });
        }

        return ticks;
      },

      [
        range.max,
        range.min,
      ],
    );

  const tone =
    schedule
      ? getScheduleTone(
          schedule,
        )
      : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width:
            'calc(100vw - 40px)',

          maxWidth:
            1500,

          maxHeight:
            'calc(100vh - 48px)',

          borderRadius:
            3.5,

          border:
            `1px solid ${alpha(
              '#38bdf8',
              0.22,
            )}`,

          bgcolor:
            '#0f1b2f',

          backgroundImage:
            'none',
        },
      }}
    >
      <DialogTitle
        sx={{ p: 0 }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            px: 2,
            py: 1.4,

            borderBottom:
              `1px solid ${alpha(
                '#94a3b8',
                0.14,
              )}`,
          }}
        >
          <TimelineRounded
            sx={{
              color:
                '#38bdf8',
            }}
          />

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                color:
                  '#f8fafc',

                fontWeight:
                  950,
              }}
            >
              Planificación del expediente
            </Typography>

            <Typography
              sx={{
                color:
                  '#94a3b8',

                fontSize:
                  '0.78rem',
              }}
            >
              Plan base, previsión vigente y recorrido temporal por etapas.
            </Typography>
          </Box>

          <IconButton
            aria-label="Cerrar planificación"
            onClick={
              onClose
            }
            sx={{
              color:
                '#94a3b8',
            }}
          >
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 2,
          py: 1.7,
        }}
      >
        <Stack
          spacing={1.25}
        >
          <Stack
            direction={{
              xs:
                'column',

              md:
                'row',
            }}
            justifyContent="space-between"
            spacing={0.8}
          >
            <Box>
              <Typography
                sx={{
                  color:
                    '#f8fafc',

                  fontWeight:
                    950,
                }}
              >
                {
                  workflowCase
                    ?.title
                  ?? 'Expediente'
                }
              </Typography>

              <Typography
                sx={{
                  color:
                    '#7dd3fc',

                  fontSize:
                    '0.78rem',
                }}
              >
                {
                  workflowCase
                    ?.reference_code
                  ?? '—'
                }
              </Typography>
            </Box>

            {tone ? (
              <Chip
                icon={
                  schedule
                    ?.active_version
                    ?.calculation_status
                  === 'ready'
                    ? (
                        <CheckCircleRounded />
                      )
                    : (
                        <WarningAmberRounded />
                      )
                }
                label={
                  tone[0]
                }
                sx={{
                  alignSelf: {
                    xs:
                      'flex-start',

                    md:
                      'center',
                  },

                  color:
                    tone[1],

                  bgcolor:
                    alpha(
                      tone[2],
                      0.16,
                    ),

                  fontWeight:
                    900,
                }}
              />
            ) : null}
          </Stack>

          {
            scheduleQuery
              .isFetching
              ? (
                  <Stack
                    direction="row"
                    spacing={0.7}
                    alignItems="center"
                  >
                    <CircularProgress
                      size={18}
                    />

                    <Typography
                      sx={{
                        color:
                          '#94a3b8',

                        fontSize:
                          '0.82rem',
                      }}
                    >
                      Cargando planificación...
                    </Typography>
                  </Stack>
                )
              : null
          }

          {
            scheduleQuery
              .isError
              ? (
                  <Alert
                    severity="error"
                    variant="outlined"
                  >
                    {
                      getErrorMessage(
                        scheduleQuery
                          .error,
                      )
                    }
                  </Alert>
                )
              : null
          }

          {schedule ? (
            <>
              <Box
                sx={{
                  display:
                    'grid',

                  gridTemplateColumns: {
                    xs:
                      'repeat(2, minmax(0, 1fr))',

                    md:
                      'repeat(5, minmax(0, 1fr))',
                  },

                  gap:
                    0.75,
                }}
              >
                <Metric
                  label="Objetivo global"
                  value={
                    formatOperationalDate(
                      schedule
                        .target_due_on,

                      'Sin objetivo',
                    )
                  }
                  helper="Compromiso del expediente"
                />

                <Metric
                  label="Plan inicial"
                  value={
                    formatOperationalDate(
                      baselineCompletion,
                    )
                  }
                  helper="Baseline congelado"
                />

                <Metric
                  label="Previsión actual"
                  value={
                    formatOperationalDate(
                      projectedCompletion,
                    )
                  }
                  helper="Recorrido vigente"
                />

                <Metric
                  label={
                    getSlackMetricLabel(
                      schedule
                        .active_version
                        ?.slack_days,
                    )
                  }
                  value={
                    formatSlackDays(
                      schedule
                        .active_version
                        ?.slack_days,

                      Boolean(
                        schedule
                          .target_due_on,
                      ),
                    )
                  }
                  helper={
                    getSlackMetricHelper(
                      schedule
                        .active_version
                        ?.slack_days,

                      Boolean(
                        schedule
                          .target_due_on,
                      ),
                    )
                  }
                />

                <Metric
                  label="Desviación sobre plan"
                  value={
                    formatDeviationDays(
                      deviationDays,
                    )
                  }
                  helper="Comparación frente al baseline inicial"
                />
              </Box>

              {
                schedule
                  .active_version
                  ?.calculation_status
                !== 'ready'
                  ? (
                      <Alert
                        severity="warning"
                        variant="outlined"
                      >
                        La previsión no está completa. Revisa la ruta estándar antes de utilizarla para tomar decisiones.
                      </Alert>
                    )
                  : null
              }

              <Paper
                elevation={0}
                sx={{
                  borderRadius:
                    3,

                  border:
                    `1px solid ${alpha(
                      '#94a3b8',
                      0.14,
                    )}`,

                  bgcolor:
                    'rgba(2,6,23,0.22)',

                  overflow:
                    'hidden',
                }}
              >
                <Stack
                  direction={{
                    xs:
                      'column',

                    md:
                      'row',
                  }}
                  justifyContent="space-between"
                  spacing={0.7}
                  sx={{
                    px:
                      1.15,

                    py:
                      0.95,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.6}
                    alignItems="center"
                  >
                    <RouteRounded
                      sx={{
                        color:
                          '#38bdf8',
                      }}
                    />

                    <Box>
                      <Typography
                        sx={{
                          color:
                            '#f8fafc',

                          fontWeight:
                            950,
                        }}
                      >
                        Recorrido temporal por etapas
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{
                          mt:
                            0.35,
                        }}
                      >
                        <Chip
                          size="small"
                          label="Gris · plan inicial"
                          sx={{
                            height:
                              19,

                            bgcolor:
                              alpha(
                                '#94a3b8',
                                0.12,
                              ),

                            color:
                              '#cbd5e1',

                            '& .MuiChip-label': {
                              px:
                                0.7,

                              fontSize:
                                '0.63rem',
                            },
                          }}
                        />

                        <Chip
                          size="small"
                          label="Color · previsión"
                          sx={{
                            height:
                              19,

                            bgcolor:
                              alpha(
                                '#38bdf8',
                                0.12,
                              ),

                            color:
                              '#7dd3fc',

                            '& .MuiChip-label': {
                              px:
                                0.7,

                              fontSize:
                                '0.63rem',
                            },
                          }}
                        />

                        <Chip
                          size="small"
                          label="Rojo · objetivo"
                          sx={{
                            height:
                              19,

                            bgcolor:
                              alpha(
                                '#ef4444',
                                0.12,
                              ),

                            color:
                              '#fca5a5',

                            '& .MuiChip-label': {
                              px:
                                0.7,

                              fontSize:
                                '0.63rem',
                            },
                          }}
                        />
                      </Stack>
                    </Box>
                  </Stack>

                  <Chip
                    size="small"
                    label={
                      `${
                        stageItems
                          .length
                      } etapas`
                    }
                  />
                </Stack>

                {
                  !stageItems
                    .length
                    ? (
                        <Alert
                          severity="info"
                          variant="outlined"
                          sx={{ m: 1 }}
                        >
                          Todavía no hay etapas planificadas para mostrar.
                        </Alert>
                      )
                    : (
                        <Box
                          sx={{
                            width: 1,
                            minWidth: 0,
                            overflowX:
                              'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: 1,
                              minWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display:
                                  'grid',

                                gridTemplateColumns: {
                                  xs:
                                    'minmax(150px, 190px) minmax(0, 1fr)',

                                  md:
                                    '190px minmax(0, 1fr)',
                                },

                                gap:
                                  1,

                                px:
                                  1.1,

                                pt:
                                  0.8,

                                pb:
                                  0.45,
                              }}
                            >
                              <Typography
                                sx={{
                                  color:
                                    '#64748b',

                                  fontSize:
                                    '0.7rem',

                                  fontWeight:
                                    900,
                                }}
                              >
                                ETAPA
                              </Typography>

                              <Box
                                sx={{
                                  position:
                                    'relative',

                                  height: 46,
                                }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    sx={{
                                      color:
                                        '#64748b',

                                      fontSize:
                                        '0.7rem',
                                    }}
                                  >
                                    {
                                      formatDayNumber(
                                        range.min,
                                      )
                                    }
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color:
                                        '#64748b',

                                      fontSize:
                                        '0.7rem',
                                    }}
                                  >
                                    {
                                      formatDayNumber(
                                        range.max,
                                      )
                                    }
                                  </Typography>
                                </Stack>

                                {timelineTicks.map(
                                  (
                                    tick,
                                  ) => (
                                    <Box
                                      key={
                                        tick.value
                                      }
                                      sx={{
                                        position:
                                          'absolute',

                                        left:
                                          `${tick.position}%`,

                                        top:
                                          0,

                                        bottom:
                                          0,

                                        transform:
                                          'translateX(-50%)',

                                        zIndex:
                                          1,

                                        pointerEvents:
                                          'none',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width:
                                            '1px',

                                          height:
                                            20,

                                          mx:
                                            'auto',

                                          bgcolor:
                                            alpha(
                                              tick.isMonthStart
                                                ? '#94a3b8'
                                                : '#64748b',
                                              tick.isMonthStart
                                                ? 0.36
                                                : 0.22,
                                            ),
                                        }}
                                      />

                                      <Typography
                                        sx={{
                                          mt:
                                            0.25,

                                          color:
                                            tick.isMonthStart
                                              ? '#94a3b8'
                                              : '#64748b',

                                          fontSize:
                                            '0.62rem',

                                          fontWeight:
                                            tick.isMonthStart
                                              ? 850
                                              : 700,

                                          whiteSpace:
                                            'nowrap',

                                          transform:
                                            'translateX(-50%)',
                                        }}
                                      >
                                        {
                                          tick.label
                                        }
                                      </Typography>
                                    </Box>
                                  ),
                                )}

                                {todayPosition !== null ? (
                                  <Box
                                    sx={{
                                      position:
                                        'absolute',
                                      left: `${todayPosition}%`,
                                      top: 12,
                                      transform:
                                        'translateX(-50%)',
                                      zIndex: 4,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color:
                                          '#e2e8f0',
                                        fontSize:
                                          '0.65rem',
                                        fontWeight:
                                          900,
                                      }}
                                    >
                                      Hoy · {
                                        formatOperationalDate(
                                          getLocalDateOnly(),
                                        )
                                      }
                                    </Typography>
                                  </Box>
                                ) : null}

                                {targetPosition !== null ? (
                                  <Box
                                    sx={{
                                      position:
                                        'absolute',
                                      left: `${targetPosition}%`,
                                      top: 12,
                                      transform:
                                        'translateX(-50%)',
                                      zIndex: 4,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color:
                                          '#fca5a5',
                                        fontSize:
                                          '0.65rem',
                                        fontWeight:
                                          900,
                                      }}
                                    >
                                      Objetivo · {
                                        formatOperationalDate(
                                          schedule
                                            ?.target_due_on,
                                        )
                                      }
                                    </Typography>
                                  </Box>
                                ) : null}

                                {projectedPosition !== null ? (
                                  <Box
                                    sx={{
                                      position:
                                        'absolute',

                                      left:
                                        `${projectedPosition}%`,

                                      top:
                                        12,

                                      transform:
                                        'translateX(-50%)',

                                      zIndex:
                                        4,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color:
                                          '#c4b5fd',

                                        fontSize:
                                          '0.65rem',

                                        fontWeight:
                                          900,

                                        whiteSpace:
                                          'nowrap',
                                      }}
                                    >
                                      Fin previsto · {
                                        formatOperationalDate(
                                          projectedCompletion,
                                        )
                                      }
                                    </Typography>
                                  </Box>
                                ) : null}
                              </Box>
                            </Box>

                            {[
                              {
                                label:
                                  'Completadas',
                                items:
                                  completedStageItems,
                              },
                              {
                                label:
                                  'Etapa actual',
                                items:
                                  currentStageItems,
                              },
                              {
                                label:
                                  'Próximas',
                                items:
                                  futureStageItems,
                              },
                            ]
                              .filter(
                                (group) =>
                                  group.items.length > 0,
                              )
                              .map(
                                (
                                  group,
                                ) => (
                                  <Box
                                    key={
                                      group.label
                                    }
                                  >
                                    <Box
                                      sx={{
                                        px: 1.1,
                                        py: 0.55,
                                        borderTop:
                                          `1px solid ${alpha(
                                            '#94a3b8',
                                            0.08,
                                          )}`,
                                        bgcolor:
                                          'rgba(255,255,255,0.02)',
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          color:
                                            '#94a3b8',
                                          fontSize:
                                            '0.68rem',
                                          fontWeight:
                                            900,
                                          textTransform:
                                            'uppercase',
                                          letterSpacing:
                                            0.4,
                                        }}
                                      >
                                        {
                                          group.label
                                        }
                                      </Typography>
                                    </Box>

                                    {group.items.map(
                                      (
                                        item,
                                      ) => {
                                    const role =
                                      getStageRole(
                                        item,
                                      );

                                    const baselineStyle =
                                      getBarStyle(
                                        item
                                          .planned_start_on,

                                        item
                                          .planned_due_on,
                                      );

                                    const forecastStyle =
                                      getBarStyle(
                                        item
                                          .forecast_start_on,

                                        item
                                          .forecast_due_on,
                                      );

                                    const standardDurationDays =
                                      getStandardDurationDays(
                                        item,
                                      );

                                    const forecastDurationDays =
                                      getForecastDurationDays(
                                        item,
                                      )
                                      ?? standardDurationDays;

                                    const hasManualOverride =
                                      hasManualScheduleOverride(
                                        item,
                                      );

                                    const canReplanStage =
                                      getScheduleRoleValue(
                                        item,
                                      ) === 'future'
                                      && Boolean(
                                        item
                                          .stage_definition_id,
                                      );

                                    return (
                                      <Box
                                        key={
                                          item.id
                                        }
                                        sx={{
                                          display:
                                            'grid',

                                          gridTemplateColumns:
                                            '190px minmax(0, 1fr)',

                                          gap:
                                            1,

                                          px:
                                            1.1,

                                          py:
                                            0.42,

                                          borderTop:
                                            `1px solid ${alpha(
                                              '#94a3b8',
                                              0.08,
                                            )}`,

                                          bgcolor:
                                            readMetadataText(
                                              item,
                                              'schedule_role',
                                            )
                                            === 'current'
                                              ? alpha(
                                                  '#0284c7',
                                                  0.08,
                                                )
                                              : 'transparent',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            minWidth:
                                              0,
                                          }}
                                        >
                                          <Stack
                                            direction="row"
                                            spacing={0.4}
                                            alignItems="center"
                                            flexWrap="wrap"
                                            useFlexGap
                                          >
                                            <Typography
                                              sx={{
                                                color:
                                                  '#e2e8f0',

                                                fontSize:
                                                  '0.82rem',

                                                fontWeight:
                                                  900,
                                              }}
                                            >
                                              {
                                                item
                                                  .title
                                              }
                                            </Typography>

                                            <Chip
                                              size="small"
                                              label={
                                                role[0]
                                              }
                                              sx={{
                                                display:
                                                  role[0] === 'Prevista'
                                                    ? 'none'
                                                    : 'inline-flex',

                                                height:
                                                  19,

                                                color:
                                                  role[1],

                                                bgcolor:
                                                  alpha(
                                                    role[2],
                                                    0.14,
                                                  ),

                                                '& .MuiChip-label': {
                                                  px:
                                                    0.6,

                                                  fontSize:
                                                    '0.63rem',
                                                },
                                              }}
                                            />
                                          </Stack>

                                          <Typography
                                            sx={{
                                              display:
                                                'none',
                                            }}
                                          >
                                            {
                                              getStageTypeLabel(
                                                item,
                                              )
                                            }
                                          </Typography>

                                          <Stack
                                            direction="row"
                                            spacing={0.45}
                                            flexWrap="wrap"
                                            useFlexGap
                                            sx={{
                                              mt:
                                                0.25,
                                            }}
                                          >
                                            {
                                              standardDurationDays !== null
                                              && standardDurationDays !== undefined
                                                ? (
                                                    <Chip
                                                      size="small"
                                                      label={
                                                        forecastDurationDays !== null
                                                        && forecastDurationDays !== undefined
                                                        && forecastDurationDays !== standardDurationDays
                                                          ? `${forecastDurationDays}/${standardDurationDays}d`
                                                          : `${standardDurationDays}d`
                                                      }
                                                      sx={{
                                                        height:
                                                          19,

                                                        color:
                                                          '#cbd5e1',

                                                        bgcolor:
                                                          alpha(
                                                            '#64748b',
                                                            0.18,
                                                          ),

                                                        '& .MuiChip-label': {
                                                          px:
                                                            0.6,

                                                          fontSize:
                                                            '0.63rem',
                                                        },
                                                      }}
                                                    />
                                                  )
                                                : null
                                            }

                                            {
                                              hasManualOverride
                                                ? (
                                                    <Chip
                                                      size="small"
                                                      label="Manual"
                                                      sx={{
                                                        height:
                                                          19,

                                                        color:
                                                          '#fed7aa',

                                                        bgcolor:
                                                          alpha(
                                                            '#f97316',
                                                            0.18,
                                                          ),

                                                        '& .MuiChip-label': {
                                                          px:
                                                            0.6,

                                                          fontSize:
                                                            '0.63rem',
                                                        },
                                                      }}
                                                    />
                                                  )
                                                : null
                                            }

                                            {
                                              canReplanStage
                                                ? (
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      onClick={() =>
                                                        openReplanDialog(
                                                          item,
                                                        )
                                                      }
                                                      sx={{
                                                        minHeight:
                                                          20,

                                                        py:
                                                          0,

                                                        px:
                                                          0.75,

                                                        borderRadius:
                                                          999,

                                                        textTransform:
                                                          'none',

                                                        fontSize:
                                                          '0.63rem',

                                                        fontWeight:
                                                          850,

                                                        color:
                                                          '#7dd3fc',

                                                        borderColor:
                                                          alpha(
                                                            '#38bdf8',
                                                            0.42,
                                                          ),
                                                      }}
                                                    >
                                                      Ajustar
                                                    </Button>
                                                  )
                                                : null
                                            }
                                          </Stack>
                                        </Box>

                                        <Box
                                          sx={{
                                            position:
                                              'relative',

                                            width: 1,
                                            minWidth: 0,

                                            height:
                                              32,

                                            borderRadius:
                                              1.4,

                                            background:
                                              'transparent !important',

                                            backgroundColor:
                                              'transparent !important',

                                            backgroundImage:
                                              'none !important',

                                            boxShadow:
                                              'none !important',

                                            '&::before, &::after': {
                                              content:
                                                'none',

                                              display:
                                                'none !important',
                                            },

                                            overflow:
                                              'visible',
                                          }}
                                        >
                                          {timelineTicks.map(
                                            (
                                              tick,
                                            ) => (
                                              <Box
                                                key={
                                                  tick.value
                                                }
                                                sx={{
                                                  position:
                                                    'absolute',

                                                  top:
                                                    0,

                                                  bottom:
                                                    0,

                                                  left:
                                                    `${tick.position}%`,

                                                  width:
                                                    '1px',

                                                  bgcolor:
                                                    alpha(
                                                      tick.isMonthStart
                                                        ? '#94a3b8'
                                                        : '#64748b',
                                                      tick.isMonthStart
                                                        ? 0.16
                                                        : 0.09,
                                                    ),

                                                  zIndex:
                                                    1,

                                                  pointerEvents:
                                                    'none',
                                                }}
                                              />
                                            ),
                                          )}

                                          {
                                            todayPosition
                                            !== null
                                              ? (
                                                  <Box
                                                    sx={{
                                                      position:
                                                        'absolute',

                                                      top: 2,
                                                      bottom: 2,

                                                      left:
                                                        `${todayPosition}%`,

                                                      width:
                                                        '1px',

                                                      bgcolor:
                                                        alpha(
                                                          '#f8fafc',
                                                          0.55,
                                                        ),

                                                      zIndex:
                                                        2,
                                                    }}
                                                  />
                                                )
                                              : null
                                          }

                                          {
                                            targetPosition
                                            !== null
                                              ? (
                                                  <Box
                                                    sx={{
                                                      position:
                                                        'absolute',

                                                      top: 2,
                                                      bottom: 2,

                                                      left:
                                                        `${targetPosition}%`,

                                                      width:
                                                        '2px',

                                                      bgcolor:
                                                        alpha(
                                                          '#ef4444',
                                                          0.75,
                                                        ),

                                                      zIndex:
                                                        2,
                                                    }}
                                                  />
                                                )
                                              : null
                                          }

                                          {
                                            baselineStyle
                                              ? (
                                                  <Box
                                                    sx={{
                                                      position:
                                                        'absolute',

                                                      top:
                                                        4,

                                                      height:
                                                        7,

                                                      borderRadius:
                                                        999,

                                                      bgcolor:
                                                        alpha(
                                                          '#e2e8f0',
                                                          0.45,
                                                        ),

                                                      ...baselineStyle,
                                                    }}
                                                  />
                                                )
                                              : null
                                          }

                                          {
                                            forecastStyle
                                              ? (
                                                  <Box
                                                    sx={{
                                                      position:
                                                        'absolute',

                                                      top:
                                                        14,

                                                      height:
                                                        16,

                                                      borderRadius:
                                                        999,

                                                      boxShadow:
                                                        `0 0 0 1px ${alpha(
                                                          '#0f172a',
                                                          0.45,
                                                        )}`,

                                                      bgcolor:
                                                        getForecastColor(
                                                          item,
                                                        ),

                                                      ...forecastStyle,
                                                    }}
                                                  />
                                                )
                                              : null
                                          }

                                          {getScheduleRoleValue(
                                            item,
                                          ) === 'current'
                                          && getPosition(
                                            getOperationalDueOn(
                                              item,
                                            ),
                                          ) !== null ? (
                                            <Box
                                              sx={{
                                                position:
                                                  'absolute',

                                                top: 10,
                                                bottom: 4,

                                                left: `${getPosition(
                                                  getOperationalDueOn(
                                                    item,
                                                  ),
                                                )}%`,

                                                width:
                                                  '2px',

                                                bgcolor:
                                                  '#22d3ee',

                                                zIndex:
                                                  4,
                                              }}
                                            />
                                          ) : null}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                  </Box>
                                ),
                              )}
                          </Box>
                        </Box>
                      )
                }
              </Paper>
            </>
          ) : null}
        </Stack>
      </DialogContent>

      <Dialog
        open={Boolean(
          replanItem,
        )}
        onClose={
          previewOverrideMutation.isPending
          || applyOverrideMutation.isPending
            ? undefined
            : closeReplanDialog
        }
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius:
              3,

            border:
              `1px solid ${alpha(
                '#38bdf8',
                0.22,
              )}`,

            bgcolor:
              '#0f1b2f',

            backgroundImage:
              'none',
          },
        }}
      >
        <DialogTitle>
          <Typography
            sx={{
              color:
                '#f8fafc',

              fontWeight:
                950,
            }}
          >
            Ajustar previsión futura
          </Typography>

          <Typography
            sx={{
              color:
                '#94a3b8',

              fontSize:
                '0.78rem',
            }}
          >
            Este cambio solo afecta a este expediente. No modifica la duración estándar de la etapa.
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={1.2}
            sx={{
              pt:
                0.5,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius:
                  2,

                border:
                  `1px solid ${alpha(
                    '#94a3b8',
                    0.14,
                  )}`,

                bgcolor:
                  'rgba(2,6,23,0.22)',

                p:
                  1,
              }}
            >
              <Typography
                sx={{
                  color:
                    '#f8fafc',

                  fontWeight:
                    900,
                }}
              >
                {
                  replanItem
                    ?.title
                  ?? 'Etapa'
                }
              </Typography>

              <Typography
                sx={{
                  color:
                    '#94a3b8',

                  fontSize:
                    '0.75rem',
                }}
              >
                Previsión actual:{' '}
                {
                  formatOperationalDate(
                    replanItem
                      ?.forecast_start_on,
                  )
                }
                {' → '}
                {
                  formatOperationalDate(
                    replanItem
                      ?.forecast_due_on,
                  )
                }
              </Typography>
            </Paper>

            <TextField
              label="Nueva duración prevista en días laborables"
              type="number"
              value={
                durationInput
              }
              onChange={(
                event,
              ) => {
                setDurationInput(
                  event.target.value,
                );

                setOverridePreview(
                  null,
                );
              }}
              fullWidth
              size="small"
            />

            <TextField
              label="Motivo obligatorio"
              value={
                reasonInput
              }
              onChange={(
                event,
              ) =>
                setReasonInput(
                  event.target.value,
                )
              }
              fullWidth
              multiline
              minRows={3}
              size="small"
              placeholder="Ej.: El cliente confirma respuesta más rápida y se ajusta la previsión de esta etapa."
            />

            {
              replanError
                ? (
                    <Alert
                      severity="error"
                      variant="outlined"
                    >
                      {
                        getErrorMessage(
                          replanError,
                        )
                      }
                    </Alert>
                  )
                : null
            }

            {
              overridePreview
                ? (
                    <Alert
                      severity={
                        (
                          overridePreview.completion_improvement_days
                          ?? 0
                        ) >= 0
                          ? 'success'
                          : 'warning'
                      }
                      variant="outlined"
                    >
                      <Typography
                        sx={{
                          fontWeight:
                            900,
                        }}
                      >
                        Impacto previsto
                      </Typography>

                      <Typography
                        sx={{
                          fontSize:
                            '0.82rem',
                        }}
                      >
                        Fin de etapa:{' '}
                        {
                          formatOperationalDate(
                            overridePreview
                              .current_forecast_due_on,
                          )
                        }
                        {' → '}
                        {
                          formatOperationalDate(
                            overridePreview
                              .preview_forecast_due_on,
                          )
                        }
                      </Typography>

                      <Typography
                        sx={{
                          fontSize:
                            '0.82rem',
                        }}
                      >
                        Fin previsto del expediente:{' '}
                        {
                          formatOperationalDate(
                            overridePreview
                              .current_projected_completion_on,
                          )
                        }
                        {' → '}
                        {
                          formatOperationalDate(
                            overridePreview
                              .projected_completion_on,
                          )
                        }
                      </Typography>

                      <Typography
                        sx={{
                          fontSize:
                            '0.82rem',
                        }}
                      >
                        Margen/retraso:{' '}
                        {
                          overridePreview
                            .current_slack_days
                          ?? '—'
                        }
                        {' → '}
                        {
                          overridePreview
                            .slack_days
                          ?? '—'
                        }
                        {' días laborables'}
                      </Typography>
                    </Alert>
                  )
                : null
            }
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px:
              3,

            pb:
              2,
          }}
        >
          <Button
            onClick={
              closeReplanDialog
            }
            disabled={
              previewOverrideMutation.isPending
              || applyOverrideMutation.isPending
            }
            sx={{
              textTransform:
                'none',
            }}
          >
            Cancelar
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            onClick={() =>
              previewOverrideMutation.mutate()
            }
            disabled={
              previewOverrideMutation.isPending
              || applyOverrideMutation.isPending
            }
            sx={{
              textTransform:
                'none',

              fontWeight:
                850,
            }}
          >
            {
              previewOverrideMutation.isPending
                ? 'Calculando...'
                : 'Previsualizar'
            }
          </Button>

          <Button
            variant="contained"
            onClick={() =>
              applyOverrideMutation.mutate()
            }
            disabled={
              !reasonInput.trim()
              || !durationInput.trim()
              || previewOverrideMutation.isPending
              || applyOverrideMutation.isPending
            }
            sx={{
              textTransform:
                'none',

              fontWeight:
                900,
            }}
          >
            {
              applyOverrideMutation.isPending
                ? 'Aplicando...'
                : overridePreview
                  ? 'Aplicar replanificación'
                  : 'Aplicar sin previsualizar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      <DialogActions
        sx={{
          px: 2,
          py: 1.1,
          borderTop: `1px solid ${alpha(
            '#94a3b8',
            0.12,
          )}`,
        }}
      >
        <Typography
          sx={{
            color:
              '#64748b',

            fontSize:
              '0.72rem',
          }}
        >
          La previsión se actualiza automáticamente al modificar el expediente.
        </Typography>

        <Box
          sx={{
            flex: 1,
          }}
        />

        <Button
          onClick={onClose}
          sx={{
            textTransform:
              'none',

            fontWeight:
              850,
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}